import { useCallback, useState } from "react";
import { Link } from "@tanstack/react-router";
import { copyText } from "@/lib/copy";
import type {
  PresentationVariants,
  SkinToneVariant,
  ZwjMember,
} from "@/lib/emoji-variants";

interface Props {
  symbolName: string;
  skinTones: SkinToneVariant[] | null;
  presentation: PresentationVariants | null;
  zwjMembers: ZwjMember[] | null;
}

function CopyChip({
  value,
  label,
  ariaLabel,
  big,
}: {
  value: string;
  label: string;
  ariaLabel: string;
  big?: boolean;
}) {
  const [copied, setCopied] = useState(false);
  const copy = useCallback(async () => {
    const ok = await copyText(value, { source: "symbol-variant", symbol: value });
    if (!ok) return;
    setCopied(true);
    window.setTimeout(() => setCopied(false), 1200);
  }, [value]);
  return (
    <button
      type="button"
      onClick={copy}
      aria-label={ariaLabel}
      className="glass flex w-full items-center gap-3 rounded-lg px-3 py-2 text-left transition-colors hover:border-primary/40"
      title={ariaLabel}
    >
      <span
        aria-hidden="true"
        className={
          big
            ? "flex h-12 w-12 flex-none items-center justify-center rounded-md border border-border bg-surface/60 text-2xl"
            : "flex h-10 w-10 flex-none items-center justify-center rounded-md border border-border bg-surface/60 text-xl"
        }
      >
        {value}
      </span>
      <span className="flex min-w-0 flex-1 flex-col">
        <span className="truncate text-sm font-medium">{label}</span>
        <span className="truncate font-mono text-[11px] text-muted-foreground">
          {copied ? "Copied!" : value}
        </span>
      </span>
      <span className="text-xs text-muted-foreground">copy</span>
    </button>
  );
}

export function SymbolVariants({ symbolName, skinTones, presentation, zwjMembers }: Props) {
  if (!skinTones && !presentation && !zwjMembers) return null;
  return (
    <>
      {skinTones ? (
        <section
          aria-labelledby="skin-tone-heading"
          className="mx-auto w-full max-w-5xl px-4 pt-10 sm:px-6 sm:pt-12"
        >
          <h2 id="skin-tone-heading" className="font-display text-xl font-semibold sm:text-2xl">
            Skin tone variants
          </h2>
          <p className="mt-1 text-sm text-muted-foreground">
            The {symbolName.toLowerCase()} accepts a Fitzpatrick skin-tone
            modifier (U+1F3FB–U+1F3FF). Each variant is a distinct copyable
            sequence — click to copy.
          </p>
          <ul className="mt-5 grid grid-cols-1 gap-2 sm:grid-cols-2 lg:grid-cols-3">
            {skinTones.map((v) => (
              <li key={v.tone}>
                <CopyChip
                  value={v.symbol}
                  label={`${v.label} · ${v.fitzpatrick}`}
                  ariaLabel={`Copy ${symbolName} with ${v.label.toLowerCase()}`}
                  big
                />
              </li>
            ))}
          </ul>
        </section>
      ) : null}

      {presentation ? (
        <section
          aria-labelledby="presentation-heading"
          className="mx-auto w-full max-w-5xl px-4 pt-10 sm:px-6 sm:pt-12"
        >
          <h2 id="presentation-heading" className="font-display text-xl font-semibold sm:text-2xl">
            Text vs emoji presentation
          </h2>
          <p className="mt-1 text-sm text-muted-foreground">
            This codepoint has two standardized presentations. Append{" "}
            <code className="rounded bg-surface/60 px-1 font-mono text-xs">U+FE0E</code>{" "}
            to force the monochrome text form, or{" "}
            <code className="rounded bg-surface/60 px-1 font-mono text-xs">U+FE0F</code>{" "}
            to force the colorful emoji form. Default presentation:{" "}
            <strong className="text-foreground">{presentation.defaultPresentation}</strong>.
          </p>
          <ul className="mt-5 grid grid-cols-1 gap-2 sm:grid-cols-2">
            <li>
              <CopyChip
                value={presentation.text}
                label="Text presentation (VS15)"
                ariaLabel={`Copy ${symbolName} text presentation`}
                big
              />
            </li>
            <li>
              <CopyChip
                value={presentation.emoji}
                label="Emoji presentation (VS16)"
                ariaLabel={`Copy ${symbolName} emoji presentation`}
                big
              />
            </li>
          </ul>
        </section>
      ) : null}

      {zwjMembers ? (
        <section
          aria-labelledby="zwj-heading"
          className="mx-auto w-full max-w-5xl px-4 pt-10 sm:px-6 sm:pt-12"
        >
          <h2 id="zwj-heading" className="font-display text-xl font-semibold sm:text-2xl">
            Sequence members
          </h2>
          <p className="mt-1 text-sm text-muted-foreground">
            {symbolName} is an emoji ZWJ sequence — the renderer joins the
            following codepoints with a zero-width joiner (U+200D).
          </p>
          <ul className="mt-5 grid grid-cols-1 gap-2 sm:grid-cols-2 lg:grid-cols-3">
            {zwjMembers.map((m, i) => (
              <li key={i}>
                {m.slug ? (
                  <Link
                    to="/symbol/$slug"
                    params={{ slug: m.slug }}
                    className="glass flex items-center gap-3 rounded-lg px-3 py-2 text-sm transition-colors hover:border-primary/40"
                    title={`${m.name} — view symbol`}
                  >
                    <span className="flex h-10 w-10 flex-none items-center justify-center rounded-md border border-border bg-surface/60 text-xl">
                      {m.symbol}
                    </span>
                    <span className="flex min-w-0 flex-1 flex-col">
                      <span className="truncate text-sm font-medium">{m.name}</span>
                      <span className="text-xs text-muted-foreground">View symbol →</span>
                    </span>
                  </Link>
                ) : (
                  <CopyChip
                    value={m.symbol}
                    label={m.name}
                    ariaLabel={`Copy ${m.name}`}
                  />
                )}
              </li>
            ))}
          </ul>
        </section>
      ) : null}
    </>
  );
}
